import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { ListChecks, X } from "lucide-react";

/** Επιλογή πινακίων (πλήρη labels) που υπάγονται σε Τομέα — για «Τα πινάκιά μου» Υπευθύνων Τομέα και ειδοποιήσεις. */
export const SectorPinakiaPicker = ({ sector, courtType, isAdmin, onChange }) => {
  const [open, setOpen] = useState(false);
  const [all, setAll] = useState([]);
  const [q, setQ] = useState("");
  const [draft, setDraft] = useState([]);
  const selected = Array.isArray(sector.pinakio_labels) ? sector.pinakio_labels : [];

  useEffect(() => {
    if (!open) return;
    setDraft(selected);
    axios.get(`${API}/pretrial-sectors/pinakio-labels`, { params: { court_type: courtType } })
      .then((r) => setAll(r.data?.labels || [])).catch(() => setAll([]));
    // eslint-disable-next-line
  }, [open]);

  const visible = useMemo(() => {
    const k = q.trim().toLowerCase();
    return all.filter((x) => !k || x.label.toLowerCase().includes(k));
  }, [all, q]);
  const sn = sector.sector_number;

  return (
    <div className="mb-3" data-testid={`sa-pinakia-${sn}`}>
      <div className="flex items-center justify-between">
        <Label className="text-xs" title="Τα πινάκια που ανήκουν στον τομέα — καθορίζουν τι βλέπει ο Υπεύθυνος Τομέα («Τα πινάκιά μου», Ημερολόγιο Έδρας) και τις ειδοποιήσεις">
          Πινάκια Τομέα ({selected.length})
        </Label>
        {isAdmin && (
          <Button size="sm" variant="ghost" className="h-6 px-2 text-xs text-blue-700" onClick={() => setOpen(true)} data-testid={`sa-pinakia-edit-${sn}`}>
            <ListChecks className="w-3 h-3 mr-1" /> Επιλογή πινακίων
          </Button>
        )}
      </div>
      <div className="flex flex-wrap gap-1 mt-1">
        {selected.length === 0 && <span className="text-[11px] text-slate-400 italic">— κανένα πινάκιο (ο Υπεύθυνος Τομέα δεν θα βλέπει δικασίμους) —</span>}
        {selected.map((lb) => (
          <Badge key={lb} variant="outline" className="text-[11px] font-mono border-blue-200 text-blue-800 bg-blue-50/40" data-testid={`sa-pinakia-chip-${sn}-${lb}`}>
            {lb}
            {isAdmin && <button type="button" className="ml-1 text-slate-400 hover:text-red-600" onClick={() => onChange(selected.filter((x) => x !== lb))} data-testid={`sa-pinakia-remove-${sn}-${lb}`}><X className="w-3 h-3" /></button>}
          </Badge>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Πινάκια Τομέα {sn} — {courtType}</DialogTitle>
            <DialogDescription>Επιλέξτε τα πινάκια που υπάγονται στον τομέα. Πινάκια που ανήκουν ήδη σε άλλον τομέα σημειώνονται.</DialogDescription>
          </DialogHeader>
          <input type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Αναζήτηση πινακίου…" className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md" data-testid={`sa-pinakia-search-${sn}`} />
          <div className="max-h-[50vh] overflow-auto border rounded divide-y" data-testid={`sa-pinakia-list-${sn}`}>
            {visible.length === 0 && <p className="p-3 text-xs text-slate-400">Δεν βρέθηκαν πινάκια</p>}
            {visible.map((x) => {
              const others = (x.sectors || []).filter((n) => n !== sn);
              const checked = draft.includes(x.label);
              return (
                <label key={x.label} className="flex items-center gap-3 px-3 py-1.5 text-sm cursor-pointer hover:bg-slate-50">
                  <Checkbox checked={checked} onCheckedChange={(v) => setDraft((d) => (v ? [...d, x.label] : d.filter((l) => l !== x.label)))} data-testid={`sa-pinakia-check-${sn}-${x.label}`} />
                  <span className="font-mono flex-1">{x.label}</span>
                  <span className="text-[11px] text-slate-500">{x.cases} υποθ. · {x.hearing_dates} δικάσ.</span>
                  {others.length > 0 && <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700">Τομέας {others.join(", ")}</Badge>}
                </label>
              );
            })}
          </div>
          <DialogFooter>
            <span className="text-xs text-slate-500 mr-auto self-center">Επιλεγμένα: {draft.length}</span>
            <Button variant="outline" onClick={() => setOpen(false)}>Άκυρο</Button>
            <Button onClick={() => { onChange(draft); setOpen(false); }} data-testid={`sa-pinakia-apply-${sn}`}>Εφαρμογή</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
